// src/features/rrhh/mensajeria/components/ChatSidebarEmpleados.jsx
import {
  Avatar,
  Box,
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMemo, useState } from "react";

export default function ChatSidebarEmpleados({
  empleados = [],
  areasDisponibles = [],
  loading = false,
  selectedId,
  onSelect,
  search,
  setSearch,
  area,
  setArea,
  dense = true,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const areas = useMemo(() => {
    if (areasDisponibles?.length) return areasDisponibles;
    const s = new Set(empleados.map((e) => e.area).filter(Boolean));
    return Array.from(s);
  }, [areasDisponibles, empleados]);

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar empleado..."
          size="small"
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              height: 32,
              borderRadius: "20px",
              bgcolor: (t) => t.palette?.neutral?.[50],
              boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.45)",
              "& fieldset": { border: "none" },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: (t) => t.palette?.primary?.[900], opacity: 0.9 }} />
              </InputAdornment>
            ),
          }}
        />

        <Box>
          <Button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              borderRadius: 999,
              bgcolor: (t) => t.palette?.secondary?.[500],
              color: "common.white",
              px: 1.5,
              "&:hover": { bgcolor: (t) => t.palette?.secondary?.[600] },
            }}
            endIcon={<ExpandMoreIcon />}
          >
            {area ? `Área: ${area}` : "Seleccionar área"}
          </Button>

          <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
            <MenuItem
              onClick={() => {
                setArea("");
                setAnchorEl(null);
              }}
            >
              Todas las áreas
            </MenuItem>
            {areas.map((a) => (
              <MenuItem
                key={a}
                onClick={() => {
                  setArea(a);
                  setAnchorEl(null);
                }}
              >
                {a}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Stack>

      <Box
        sx={{
          p: 1,
          borderRadius: "20px",
          bgcolor: (t) => t.palette?.neutral?.[100],
          boxShadow: "inset 0px 0px 4px rgba(165, 165, 178, 0.9)",
          height: "50.6vh",
          overflow: "hidden",
          display: "flex",
        }}
      >
        {/* Wrapper scrolleable */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
          {loading ? (
            <Typography sx={{ p: 1, opacity: 0.75 }}>Cargando…</Typography>
          ) : empleados.length === 0 ? (
            <Stack
              spacing={1}
              sx={{
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 4,
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "primary.900" }}>
                Sin resultados
              </Typography>
              <Typography sx={{ fontSize: 14, opacity: 0.8 }}>
                No hay empleados para mostrar con los filtros actuales.
              </Typography>
            </Stack>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                gap: 1,
                alignContent: "start",
              }}
            >
              {empleados.map((e) => {
                const active = e.id === selectedId;
                const estado = String(e.estado_empleado || "").toUpperCase();
                const isActivo = estado === "ACTIVE" || estado === "ACTIVO";
                const hasUnread = Boolean(e.unreadForRrhh);
              
                return (
                  <Box
                    key={e.id}
                    onClick={() => onSelect?.(e.id)}
                    sx={{
                      p: 1,
                      borderRadius: "14px",
                      cursor: "pointer",
                      bgcolor: "common.white",
                      border: active
                        ? "2px solid rgba(52, 67, 111, 0.35)"
                        : "1px solid rgba(0,0,0,0.06)",
                      boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.22)",
                      "&:hover": { bgcolor: (t) => t.palette?.neutral?.[50] },
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      minWidth: 0,
                      position: "relative",
                    }}
                  >
                    {hasUnread ? (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: "#D92D20",
                          border: "2px solid #fff",
                          boxShadow: "0 0 0 1px rgba(217,45,32,0.25)",
                        }}
                      />
                    ) : null}

                    <Avatar
                      variant="circular"
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: (t) => t.palette?.primary?.[900],
                        color: "common.white",
                        fontWeight: 900,
                        opacity: 0.95,
                        flex: "0 0 auto",
                        boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.22)",
                      }}
                    >
                      {(String(e.nombre || "?").trim().slice(0, 1) || "?").toUpperCase()}
                    </Avatar>
                    
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 800, color: "primary.900", lineHeight: 1.4 }}
                        noWrap
                      >
                        {e.nombre}
                      </Typography>
                    
                      <Typography sx={{ fontSize: 12, opacity: 0.8, lineHeight: 1.4 }}>
                        Estado: <b>{isActivo ? "Activo" : "Inactivo"}</b>
                      </Typography>
                    
                      <Typography sx={{ fontSize: 12, opacity: 0.8, lineHeight: 1.4 }} noWrap>
                        Sucursal: <b>{e.sucursal}</b>
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Stack>
  );
}
